import { Box, Stack, Typography, useTheme } from "@mui/material";
import { type FC, useEffect } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { usePortfolioQuery } from "web_myplann/Api/Portfolio/Queries/usePortfolioQuery";
import {
	ClientPortfolioDomainEnum,
	ReminderEntityTypeEnum,
} from "web_myplann/Infrastructure/Api/Api";
import { Routing } from "web_myplann/Infrastructure/Routing/Routing";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { EventAlertStack } from "web_myplann/NotificationCenter/EventAlert/EventAlertStack";
import { REMINDERS_SECTION_ANCHOR_ID } from "web_myplann/NotificationCenter/Utils/reminderScrollParam";
import { DetailSectionLoader } from "web_myplann/Portfolio/Components/DetailSectionLoader";
import { BankProduct } from "web_myplann/Portfolio/Components/PortfolioSections/BankProduct";
import { BuildingSavings } from "web_myplann/Portfolio/Components/PortfolioSections/BuildingSavings";
import { Commodities } from "web_myplann/Portfolio/Components/PortfolioSections/Commodities";
import { Cryptocurrency } from "web_myplann/Portfolio/Components/PortfolioSections/Cryptocurrency";
import { Investment } from "web_myplann/Portfolio/Components/PortfolioSections/Investment";
import { LifeInsurance } from "web_myplann/Portfolio/Components/PortfolioSections/LifeInsurance";
import { LoanAll } from "web_myplann/Portfolio/Components/PortfolioSections/LoanAll";
import { PaidAdvisory } from "web_myplann/Portfolio/Components/PortfolioSections/PaidAdvisory";
import { PensionProduct } from "web_myplann/Portfolio/Components/PortfolioSections/PensionProduct";
import { PropertyInsurance } from "web_myplann/Portfolio/Components/PortfolioSections/PropertyInsurance";
import { Services } from "web_myplann/Portfolio/Components/PortfolioSections/Services";
import { hasNoBusinessData } from "web_myplann/Portfolio/Helpers/hasNoBusinessData";
import { usePortfolioTitle } from "web_myplann/Portfolio/Hooks/usePortfolioTitle";
import { useScrollToReminders } from "web_myplann/Portfolio/Hooks/useScrollToReminders";
import { RemindersSection } from "web_myplann/Reminders/Components/RemindersSection";
import { StyledCenteredStack } from "web_myplann/Shared/Components/StyledCenteredStack";
import { MyPlannLayout } from "web_myplann/Shared/Header/MyPlannLayout";
import NavigationHeader from "web_myplann/Shared/Header/NavigationHeader";
import { useNavigateBackOrDefault } from "web_myplann/Shared/Hooks/useNavigateBackOrDefault";
import { RowSkeletonLoader } from "web_myplann/Shared/RowSkeletonLoader";
import { DEFAULT_ROUTE } from "web_myplann/Utils/constants";

export const PortfolioDetailPageLegacy: FC = () => {
	const { id } = useParams<{ id: string }>();
	const portfolioId = id ? Number(id) : undefined;
	const { t } = useResource();
	const theme = useTheme();
	const navigateBackOrDefault = useNavigateBackOrDefault(DEFAULT_ROUTE);
	const { data: portfolio, isLoading } = usePortfolioQuery(Number(portfolioId));

	useScrollToReminders(!isLoading);

	const title = usePortfolioTitle(portfolio, t);

	const currencySign =
		portfolio?.currencySign ?? t(Resources.Common.CurrencySign);

	const isNotFound = !isLoading && !portfolio;
	const showEmpty = !isLoading && !isNotFound && hasNoBusinessData(portfolio);

	useEffect(() => {
		if (!isNotFound) return;
		toast.error(t(Resources.Portfolio.Detail.notAvailable), {
			id: "portfolio-detail-not-found",
		});
		navigateBackOrDefault();
	}, [isNotFound, navigateBackOrDefault]);

	return (
		<MyPlannLayout
			topBar={
				<NavigationHeader
					title={title}
					defaultRoute={Routing.Portfolio.PortfolioList.path()}
					replaceOnBack
				/>
			}
			disableBottomPadding
		>
			{isLoading || isNotFound ? (
				<DetailSectionLoader />
			) : showEmpty ? (
				<>
					<StyledCenteredStack spacing={2} px={5}>
						<RowSkeletonLoader />
						<RowSkeletonLoader />
						<RowSkeletonLoader />
						<Stack textAlign="center" gap={0.4}>
							<Typography
								variant="deprecatedH3"
								color={theme.palette.deprecated.grey[700]}
							>
								{t(Resources.PortfolioDetail.SharedLabels.empty)}
							</Typography>
							<Typography
								variant="deprecatedBody1"
								color={theme.palette.deprecated.grey[500]}
							>
								{t(Resources.PortfolioDetail.SharedLabels.emptySubtitle)}
							</Typography>
						</Stack>
					</StyledCenteredStack>
					{portfolio && portfolioId !== undefined && (
						<Box id={REMINDERS_SECTION_ANCHOR_ID}>
							<RemindersSection
								entity={{
									type: ReminderEntityTypeEnum.Portfolio,
									id: portfolioId,
								}}
							/>
						</Box>
					)}
				</>
			) : (
				<Stack pt={2}>
					{portfolioId !== undefined && (
						<EventAlertStack
							contractId={portfolioId}
							domain={portfolio?.domain}
							portfolio={portfolio}
						/>
					)}
					{portfolio?.domain === ClientPortfolioDomainEnum.LifeInsurance && (
						<LifeInsurance baseData={portfolio} currencySign={currencySign} />
					)}
					{portfolio?.domain === ClientPortfolioDomainEnum.Investments && (
						<Investment baseData={portfolio} currencySign={currencySign} />
					)}
					{portfolio?.domain ===
						ClientPortfolioDomainEnum.PropertyInsurance && (
						<PropertyInsurance
							baseData={portfolio}
							currencySign={currencySign}
						/>
					)}
					{portfolio?.domain === ClientPortfolioDomainEnum.PensionProducts && (
						<PensionProduct baseData={portfolio} currencySign={currencySign} />
					)}
					{portfolio?.domain === ClientPortfolioDomainEnum.BuildingSavings && (
						<BuildingSavings baseData={portfolio} currencySign={currencySign} />
					)}
					{(portfolio?.domain === ClientPortfolioDomainEnum.Loans ||
						portfolio?.domain === ClientPortfolioDomainEnum.Leasing ||
						portfolio?.domain === ClientPortfolioDomainEnum.BusinessLoans) && (
						<LoanAll baseData={portfolio} currencySign={currencySign} />
					)}
					{portfolio?.domain === ClientPortfolioDomainEnum.Commodities && (
						<Commodities baseData={portfolio} currencySign={currencySign} />
					)}
					{portfolio?.domain === ClientPortfolioDomainEnum.BankingProducts && (
						<BankProduct baseData={portfolio} currencySign={currencySign} />
					)}
					{portfolio?.domain === ClientPortfolioDomainEnum.Cryptocurrencies && (
						<Cryptocurrency baseData={portfolio} currencySign={currencySign} />
					)}
					{portfolio?.domain === ClientPortfolioDomainEnum.Services && (
						<Services baseData={portfolio} currencySign={currencySign} />
					)}
					{portfolio?.domain === ClientPortfolioDomainEnum.PaidAdvisory && (
						<PaidAdvisory baseData={portfolio} currencySign={currencySign} />
					)}
					{portfolio && portfolioId !== undefined && (
						<Box id={REMINDERS_SECTION_ANCHOR_ID}>
							<RemindersSection
								entity={{
									type: ReminderEntityTypeEnum.Portfolio,
									id: portfolioId,
								}}
							/>
						</Box>
					)}
				</Stack>
			)}
		</MyPlannLayout>
	);
};
